import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GoogleIcon from "@mui/icons-material/Google";
import { useAuth } from "./AuthContext";

const MIN_PASSWORD_LEN = 8;

/**
 * Sign in / create account dialog. Opened by AuthContext's login().
 * Email + password accounts and Google sign-in both land on the same user row.
 */
export default function AuthDialog() {
  const {
    authDialogOpen,
    closeAuthDialog,
    loginWithGoogle,
    loginWithPassword,
    registerWithPassword,
  } = useAuth();

  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (authDialogOpen) {
      setError("");
      setPassword("");
      setBusy(false);
    }
  }, [authDialogOpen]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError("");
    if (mode === "signup" && password.length < MIN_PASSWORD_LEN) {
      setError(`Password must be at least ${MIN_PASSWORD_LEN} characters.`);
      return;
    }
    setBusy(true);
    try {
      if (mode === "signup") {
        await registerWithPassword(name, email, password);
      } else {
        await loginWithPassword(email, password);
      }
    } catch (err: any) {
      setError(err?.message || "Something went wrong. Please try again.");
      setBusy(false);
    }
  };

  return (
    <Dialog open={authDialogOpen} onClose={closeAuthDialog} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span>{mode === "signup" ? "Create your account" : "Sign in"}</span>
        <IconButton onClick={closeAuthDialog} size="small" aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Tabs
          value={mode}
          onChange={(_, v) => { setMode(v); setError(""); }}
          variant="fullWidth"
          sx={{ mb: 2 }}
        >
          <Tab value="signin" label="Sign in" />
          <Tab value="signup" label="Create account" />
        </Tabs>

        <Button
          fullWidth
          variant="outlined"
          startIcon={<GoogleIcon />}
          onClick={loginWithGoogle}
          sx={{ textTransform: "none", borderRadius: "999px", py: 1 }}
        >
          Continue with Google
        </Button>

        <Divider sx={{ my: 2, fontSize: "0.75rem", color: "text.secondary" }}>or</Divider>

        <Box component="form" onSubmit={submit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {mode === "signup" && (
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              size="small"
              fullWidth
            />
          )}
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
            size="small"
            fullWidth
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete={mode === "signup" ? "new-password" : "current-password"}
            required
            size="small"
            fullWidth
            helperText={mode === "signup" ? `At least ${MIN_PASSWORD_LEN} characters` : undefined}
          />

          {error && <Alert severity="error" sx={{ py: 0 }}>{error}</Alert>}

          <Button
            type="submit"
            variant="contained"
            disabled={busy}
            sx={{
              textTransform: "none",
              borderRadius: "999px",
              py: 1,
              backgroundColor: "#3D5A80",
              "&:hover": { backgroundColor: "#32496a" },
            }}
          >
            {busy ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : mode === "signup" ? "Create account" : "Sign in"}
          </Button>
        </Box>

        <Typography variant="caption" sx={{ display: "block", mt: 2, color: "text.secondary" }}>
          {mode === "signup"
            ? "Already have an account? Switch to Sign in."
            : "New here? Switch to Create account."}
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
